import React, { useState } from 'react'
import { Card, CardActions, CardContent } from '@mui/material'
import { Box, Grid } from '@mui/system'
import { fontSizes } from '../../../Common/Typography/TypographyStyles'
import CustomBadge from '../../../Common/Badge/CustomBadge'
import { badgeColors } from '../../../Common/Color/Color'
import StarIcon from '@mui/icons-material/Star'
import EventBadges from '../../EventBadges'
import EventDateTime from './EventDateTime'
import { LoadingButton } from '@mui/lab'
import HowToRegIcon from '@mui/icons-material/HowToReg'
import useUpdateTeamMemberEvent from '../../../../hooks/eventHooks/useUpdateTeamMemberEvent'
import { useAppSelector } from '../../../../redux/store'
import useConfirmationAlert from '../../../../hooks/componentHooks/useConfirmationAlert'
import CardImage from './CardImage'
import EventViewModal from './EventViewModal'
import { EventWithAdditionalInfo } from '../../../../redux/event/teamMemberEvents/types'
import { selectUserTeamMemberInfo } from '../../../../redux/auth/selector'

interface EventCardProps {
  eventData: EventWithAdditionalInfo
}

const EventCard: React.FC<EventCardProps> = ({ eventData }) => {
  const [buttonLoading, setButtonLoading] = useState(false)
  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)

  const { handleUpdateEvent } = useUpdateTeamMemberEvent()

  const { ConfirmationAlert, openConfirmationAlert } = useConfirmationAlert()

  const onUpdateEvent = async () => {
    setButtonLoading(true)
    openConfirmationAlert({
      title: 'Confirm Registration',
      text: 'Are you sure you want to register for this event?',
      confirmButtonText: 'Yes, Register',
      cancelButtonText: 'Cancel',
      onConfirm: async () => {
        await handleUpdateEvent({
          teamMemberEmail: teamMemberDetails.email!,
          teamMemberWorkdayId: teamMemberDetails.workdayId!,
          eventId: eventData.eventDetails.id!,
          status: 'registered',
        })
        setButtonLoading(false)
      },
      onCancel: () => {
        setButtonLoading(false)
      },
    })
  }

  return (
    <React.Fragment>
      <Card raised={false}>
        <CardImage eventData={eventData.eventDetails} />
        <CardContent>
          <Box display='flex' alignItems='center' flexWrap='wrap' my={1} gap={1}>
            <EventBadges value={eventData.eventDetails.category} badgeType='categoryBadge' />

            <CustomBadge
              label={
                eventData.eventDetails.category === 'COP'
                  ? eventData.eventDetails.pointsNum > 0
                    ? `${eventData.eventDetails.pointsNum} COP Points`
                    : '0 COP Point'
                  : eventData.eventDetails.pointsNum > 0
                    ? `${eventData.eventDetails.pointsNum} Star Points`
                    : '0 Star Point'
              }
              startIcon={<StarIcon fontSize='inherit' />}
              color={badgeColors.complementaryYellow}
              size={fontSizes.size12}
            />
          </Box>

          <EventDateTime
            startDate={eventData.eventDetails.startDate}
            endDate={eventData.eventDetails.endDate}
          />
        </CardContent>
        <CardActions>
          <Grid container justifyContent='center' direction='row' size={12} spacing={1}>
            <Grid container size={{ xs: 12, xl: 6 }}>
              <EventViewModal eventData={eventData} />
            </Grid>
            <Grid container size={{ xs: 12, xl: 6 }}>
              <LoadingButton
                loading={buttonLoading}
                disabled={eventData.additionalInfo.status === 'registered'}
                loadingPosition='start'
                variant='contained'
                color='primary'
                startIcon={<HowToRegIcon fontSize='inherit' />}
                fullWidth
                onClick={onUpdateEvent}
              >
                {eventData.additionalInfo.status === 'registered' ? 'Registered' : 'Register'}
              </LoadingButton>
            </Grid>
          </Grid>
        </CardActions>
      </Card>
      {ConfirmationAlert}
    </React.Fragment>
  )
}

export default EventCard
